/**
 * formatExpiry — pure helper für Expires-At-Display in SharesListPage.
 *
 * Sprint C1 DK-8 (Codex R1 Correctness-2 fix). Existing `formatRelative()`
 * ist past-only (`Date.now() - date`) → future-timestamps unter 60s rendern
 * als „eben" — unbrauchbar für expiry. Dedizierter Helper mit klaren
 * Branches für null / future / past.
 *
 * Granularity für future-time:
 *   diff > 24h:  „in N Tagen" / „in einem Tag"
 *   diff > 1h:   „in N Stunden" / „in einer Stunde"
 *   diff > 1min: „in N Minuten" / „in einer Minute"
 *   diff < 60s:  „in Kürze"
 *   diff <= 0:   „abgelaufen am DD.MM.YYYY"
 *
 * Null branch:
 *   null:        „kein Ablauf"
 *
 * @param iso ISO-8601-Timestamp oder null
 * @param now Reference-time in ms (default Date.now()) — für deterministic tests
 */
export function formatExpiry(iso: string | null, now = Date.now()): string {
  if (iso === null) return 'kein Ablauf';

  const target = new Date(iso).getTime();
  const diffMs = target - now;

  if (diffMs <= 0) {
    return `abgelaufen am ${formatDateDE(new Date(target))}`;
  }

  if (diffMs < 60_000) {
    return 'in Kürze';
  }

  const min = Math.floor(diffMs / 60_000);
  if (min < 60) {
    return `in ${min} ${min === 1 ? 'Minute' : 'Minuten'}`;
  }

  const h = Math.floor(min / 60);
  if (h < 24) {
    return `in ${h} ${h === 1 ? 'Stunde' : 'Stunden'}`;
  }

  const d = Math.floor(h / 24);
  return `in ${d} ${d === 1 ? 'Tag' : 'Tagen'}`;
}

/** Grenze für die orange „läuft bald ab“-Pill (Redesign S2 Phase 4b): < 3 Tage. */
export const EXPIRING_SOON_MS = 3 * 24 * 60 * 60 * 1000;

export type ExpiryTone = 'soon' | 'normal' | 'expired' | 'none';

/**
 * expiryTone — Ton der Ablauf-Pill in der Share-Links-Tabelle.
 *   null → 'none' (kein Ablauf) · abgelaufen → 'expired' ·
 *   Restzeit < 3 Tage → 'soon' (orange) · sonst 'normal'.
 */
export function expiryTone(iso: string | null, now = Date.now()): ExpiryTone {
  if (iso === null) return 'none';
  const diffMs = new Date(iso).getTime() - now;
  if (diffMs <= 0) return 'expired';
  return diffMs < EXPIRING_SOON_MS ? 'soon' : 'normal';
}

/**
 * formatTransferValidity — Gültigkeits-Phrase für die „Meine Transfers"-Liste.
 * Transfers haben IMMER ein Ablaufdatum (expires_at NOT NULL). `formatExpiry`
 * liefert nur „in N Tagen" (als Relativ-Zeit gedacht); mit „gültig" davor liest
 * das falsch („gültig in 6 Tagen" ≈ wird ERST in 6 Tagen gültig). Diese Variante
 * liefert die grammatisch korrekte Ganzform: aktiv → „noch N Tage gültig",
 * abgelaufen → „abgelaufen am DD.MM.YYYY".
 */
export function formatTransferValidity(iso: string, now = Date.now()): string {
  const target = new Date(iso).getTime();
  const diffMs = target - now;

  if (diffMs <= 0) return `abgelaufen am ${formatDateDE(new Date(target))}`;
  if (diffMs < 60_000) return 'läuft gleich ab';

  const min = Math.floor(diffMs / 60_000);
  if (min < 60) return `noch ${min} ${min === 1 ? 'Minute' : 'Minuten'} gültig`;

  const h = Math.floor(min / 60);
  if (h < 24) return `noch ${h} ${h === 1 ? 'Stunde' : 'Stunden'} gültig`;

  const d = Math.floor(h / 24);
  return `noch ${d} ${d === 1 ? 'Tag' : 'Tage'} gültig`;
}

/** Format Date als DD.MM.YYYY (deutsche Konvention, lokale Zeitzone). */
export function formatDateDE(d: Date): string {
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}.${mm}.${yyyy}`;
}
